/**
 * Position Service
 *
 * Handles calculations for investment positions including:
 * - Adding to existing positions (buying more)
 * - Selling from positions
 * - Average cost basis calculations
 * - Unrealized P&L calculations
 * - Position value calculations
 *
 * Uses the Average Cost Basis method for simplicity and tax reporting.
 */

/**
 * Calculate new position values when adding shares/units to an existing position
 *
 * @param {Object} existingInvestment - Current investment state
 * @param {Object} newPurchase - New purchase details
 * @param {number} newPurchase.quantity - Number of shares/units being purchased
 * @param {number} newPurchase.pricePerUnit - Price paid per unit
 * @param {string} newPurchase.date - Date of purchase (ISO string)
 * @param {string} [newPurchase.notes] - Optional notes about the purchase
 * @returns {Object} Updated investment fields
 *
 * @example
 * // Existing: 100 shares @ $150 = $15,000 cost basis
 * // New purchase: 50 shares @ $180 = $9,000
 * // Result: 150 shares, $24,000 cost basis, $160 average
 *
 * addToPosition(
 *   { quantity: 100, averageCostBasis: 150, totalCostBasis: 15000 },
 *   { quantity: 50, pricePerUnit: 180, date: '2024-03-15' }
 * );
 * // Returns: { quantity: 150, totalCostBasis: 24000, averageCostBasis: 160, ... }
 */
export const addToPosition = (existingInvestment, newPurchase) => {
  // Validate inputs
  if (!newPurchase.quantity || newPurchase.quantity <= 0) {
    throw new Error('Purchase quantity must be greater than 0');
  }
  if (!newPurchase.pricePerUnit || newPurchase.pricePerUnit <= 0) {
    throw new Error('Price per unit must be greater than 0');
  }

  // Get existing position values (default to 0 if new investment)
  const existingQuantity = existingInvestment.quantity || 0;
  const existingCostBasis = existingInvestment.totalCostBasis ||
    (existingQuantity * (existingInvestment.averageCostBasis || 0)) || 0;

  // Calculate new purchase cost
  const newPurchaseCost = newPurchase.quantity * newPurchase.pricePerUnit;

  // Calculate updated position
  const newQuantity = existingQuantity + newPurchase.quantity;
  const newTotalCostBasis = existingCostBasis + newPurchaseCost;
  const newAverageCostBasis = newQuantity > 0 ? newTotalCostBasis / newQuantity : 0;

  // Determine purchase count
  const purchaseCount = (existingInvestment.purchaseCount || 1) + 1;

  return {
    quantity: newQuantity,
    totalCostBasis: Math.round(newTotalCostBasis * 100) / 100, // Round to 2 decimals
    averageCostBasis: Math.round(newAverageCostBasis * 100) / 100,
    lastPurchaseDate: newPurchase.date || new Date().toISOString().split('T')[0],
    purchaseCount: purchaseCount,
    // Keep original purchase date for holding period tracking
    purchaseDate: existingInvestment.purchaseDate || newPurchase.date || new Date().toISOString().split('T')[0],
    // For backward compatibility - total value at cost
    amount: Math.round(newTotalCostBasis * 100) / 100
  };
};

/**
 * Calculate position values after selling shares/units
 * Uses average cost basis method for P&L calculation
 *
 * @param {Object} existingInvestment - Current investment state
 * @param {Object} sale - Sale details
 * @param {number} sale.quantity - Number of shares/units being sold
 * @param {number} sale.pricePerUnit - Sale price per unit
 * @param {string} sale.date - Date of sale (ISO string)
 * @param {string} [sale.notes] - Optional notes about the sale
 * @returns {Object} Updated investment fields and realized P&L
 *
 * @example
 * // Existing: 150 shares @ $160 avg = $24,000 cost basis
 * // Sell: 50 shares @ $200 = $10,000 proceeds
 * // Realized gain: $10,000 - (50 * $160) = $2,000
 * // Remaining: 100 shares @ $160 avg = $16,000 cost basis
 */
export const sellFromPosition = (existingInvestment, sale) => {
  // Validate inputs
  if (!sale.quantity || sale.quantity <= 0) {
    throw new Error('Sale quantity must be greater than 0');
  }
  if (!sale.pricePerUnit || sale.pricePerUnit <= 0) {
    throw new Error('Sale price must be greater than 0');
  }
  if (!existingInvestment.quantity || sale.quantity > existingInvestment.quantity) {
    throw new Error(`Cannot sell ${sale.quantity} units - only ${existingInvestment.quantity || 0} available`);
  }

  const avgCostBasis = existingInvestment.averageCostBasis ||
    (existingInvestment.totalCostBasis / existingInvestment.quantity) || 0;

  // Calculate remaining position
  const remainingQuantity = existingInvestment.quantity - sale.quantity;
  const remainingCostBasis = remainingQuantity * avgCostBasis;

  // Calculate realized P&L for this sale (using average cost method)
  const saleProceeds = sale.quantity * sale.pricePerUnit;
  const saleCostBasis = sale.quantity * avgCostBasis;
  const realizedGainLoss = saleProceeds - saleCostBasis;
  const realizedGainLossPercent = saleCostBasis > 0
    ? (realizedGainLoss / saleCostBasis) * 100
    : 0;

  return {
    // Updated investment fields
    investment: {
      quantity: remainingQuantity,
      totalCostBasis: Math.round(remainingCostBasis * 100) / 100,
      averageCostBasis: avgCostBasis, // Average cost doesn't change on sale
      // For backward compatibility
      amount: Math.round(remainingCostBasis * 100) / 100
    },
    // Sale transaction record
    transaction: {
      type: 'sell',
      date: sale.date || new Date().toISOString().split('T')[0],
      quantity: sale.quantity,
      pricePerUnit: sale.pricePerUnit,
      totalProceeds: Math.round(saleProceeds * 100) / 100,
      costBasis: Math.round(saleCostBasis * 100) / 100,
      realizedGainLoss: Math.round(realizedGainLoss * 100) / 100,
      realizedGainLossPercent: Math.round(realizedGainLossPercent * 100) / 100,
      notes: sale.notes || ''
    }
  };
};

/**
 * Calculate current value and unrealized P&L for a position
 *
 * @param {Object} investment - Investment with quantity and cost basis
 * @param {number} currentPrice - Latest market price per unit
 * @returns {Object} Value calculations
 */
export const calculatePositionValue = (investment, currentPrice) => {
  if (!investment.quantity || investment.quantity <= 0) {
    return {
      currentValue: 0,
      unrealizedGainLoss: 0,
      unrealizedGainLossPercent: 0,
      totalCostBasis: 0
    };
  }

  const quantity = investment.quantity;
  const avgCostBasis = investment.averageCostBasis ||
    (investment.totalCostBasis / quantity) || 0;
  const totalCostBasis = investment.totalCostBasis || (quantity * avgCostBasis);

  const currentValue = quantity * currentPrice;
  const unrealizedGainLoss = currentValue - totalCostBasis;
  const unrealizedGainLossPercent = totalCostBasis > 0
    ? (unrealizedGainLoss / totalCostBasis) * 100
    : 0;

  return {
    currentValue: Math.round(currentValue * 100) / 100,
    unrealizedGainLoss: Math.round(unrealizedGainLoss * 100) / 100,
    unrealizedGainLossPercent: Math.round(unrealizedGainLossPercent * 100) / 100,
    totalCostBasis: Math.round(totalCostBasis * 100) / 100
  };
};

/**
 * Calculate portfolio-level totals from all investments
 *
 * @param {Array} investments - Array of investments with position data
 * @returns {Object} Portfolio totals
 */
export const calculatePortfolioTotals = (investments) => {
  const totals = investments.reduce((acc, inv) => {
    const costBasis = inv.totalCostBasis || inv.amount || 0;
    const currentValue = inv.currentValue || inv.amount || 0;
    const unrealizedPL = inv.unrealizedGainLoss || 0;

    return {
      totalCostBasis: acc.totalCostBasis + costBasis,
      totalCurrentValue: acc.totalCurrentValue + currentValue,
      totalUnrealizedGainLoss: acc.totalUnrealizedGainLoss + unrealizedPL,
    };
  }, { totalCostBasis: 0, totalCurrentValue: 0, totalUnrealizedGainLoss: 0 });

  // Calculate overall percentage change
  totals.totalUnrealizedGainLossPercent = totals.totalCostBasis > 0
    ? (totals.totalUnrealizedGainLoss / totals.totalCostBasis) * 100
    : 0;

  // Round all values
  return {
    totalCostBasis: Math.round(totals.totalCostBasis * 100) / 100,
    totalCurrentValue: Math.round(totals.totalCurrentValue * 100) / 100,
    totalUnrealizedGainLoss: Math.round(totals.totalUnrealizedGainLoss * 100) / 100,
    totalUnrealizedGainLossPercent: Math.round(totals.totalUnrealizedGainLossPercent * 100) / 100
  };
};

/**
 * Convert a legacy amount-based investment to quantity-based
 * Used for migrating existing investments
 *
 * @param {Object} investment - Legacy investment with 'amount' field
 * @param {number} [currentPrice] - Current market price (if known)
 * @returns {Object} Investment with quantity fields populated
 */
export const migrateToQuantityBased = (investment, currentPrice = null) => {
  // If already has quantity, return as-is
  if (investment.quantity && investment.quantity > 0) {
    return investment;
  }

  // If no amount, can't migrate
  if (!investment.amount || investment.amount <= 0) {
    return investment;
  }

  // Best case: we have current price, can calculate quantity
  if (currentPrice && currentPrice > 0) {
    const quantity = investment.amount / currentPrice;
    return {
      ...investment,
      quantity: Math.round(quantity * 10000) / 10000, // 4 decimal places for crypto
      averageCostBasis: currentPrice,
      totalCostBasis: investment.amount,
      purchaseCount: 1
    };
  }

  // Fallback: treat amount as cost basis with quantity of 1
  // User will need to manually correct this
  return {
    ...investment,
    quantity: 1,
    averageCostBasis: investment.amount,
    totalCostBasis: investment.amount,
    purchaseCount: 1,
    needsMigrationReview: true // Flag for user to review
  };
};

/**
 * Validate investment has required fields for quantity-based tracking
 *
 * @param {Object} investment - Investment to validate
 * @returns {Object} Validation result with errors and warnings
 */
export const validatePositionFields = (investment) => {
  const errors = [];
  const warnings = [];

  if (!investment.quantity || investment.quantity <= 0) {
    errors.push('Quantity is required and must be greater than 0');
  }

  if (!investment.averageCostBasis || investment.averageCostBasis <= 0) {
    errors.push('Average cost basis is required and must be greater than 0');
  }

  if (!investment.purchaseDate) {
    warnings.push('Purchase date is not set - using current date');
  }

  // Check for data consistency
  if (investment.quantity && investment.averageCostBasis) {
    const calculatedCostBasis = investment.quantity * investment.averageCostBasis;
    if (investment.totalCostBasis) {
      const diff = Math.abs(calculatedCostBasis - investment.totalCostBasis);
      if (diff > 0.01) {
        warnings.push(`Total cost basis (${investment.totalCostBasis}) doesn't match quantity × average (${calculatedCostBasis.toFixed(2)})`);
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings
  };
};

/**
 * Create a purchase transaction record
 *
 * @param {Object} purchase - Purchase details
 * @returns {Object} Formatted purchase record for storage
 */
export const createPurchaseRecord = (purchase) => {
  return {
    id: `purchase_${Date.now()}`,
    type: 'buy',
    date: purchase.date || new Date().toISOString().split('T')[0],
    quantity: purchase.quantity,
    pricePerUnit: purchase.pricePerUnit,
    totalAmount: Math.round(purchase.quantity * purchase.pricePerUnit * 100) / 100,
    fees: purchase.fees || 0,
    notes: purchase.notes || '',
    createdAt: new Date().toISOString()
  };
};

/**
 * Get unit label based on category
 *
 * @param {string} category - Investment category
 * @returns {string} Unit label (e.g., "shares", "coins", "oz")
 */
export const getUnitLabel = (category) => {
  const units = {
    'Stocks': 'shares',
    'Bonds': 'units',
    'Cryptocurrency': 'coins',
    'Commodities': 'oz', // troy ounces for metals
    'Real Estate': 'units',
    'Cash': 'units'
  };
  return units[category] || 'units';
};

/**
 * Format quantity for display based on category
 * Stocks show whole numbers, crypto shows up to 8 decimals
 *
 * @param {number} quantity - Raw quantity
 * @param {string} category - Investment category
 * @returns {string} Formatted quantity string
 */
export const formatQuantity = (quantity, category) => {
  if (category === 'Cryptocurrency') {
    // Crypto can have many decimal places
    if (quantity >= 1) {
      return quantity.toFixed(4);
    }
    return quantity.toFixed(8);
  }

  if (category === 'Commodities') {
    // Commodities typically 2-4 decimal places (troy ounces)
    return quantity.toFixed(4);
  }

  // Stocks and bonds are typically whole shares
  if (Number.isInteger(quantity)) {
    return quantity.toString();
  }

  // Fractional shares
  return quantity.toFixed(4);
};

export default {
  addToPosition,
  sellFromPosition,
  calculatePositionValue,
  calculatePortfolioTotals,
  migrateToQuantityBased,
  validatePositionFields,
  createPurchaseRecord,
  getUnitLabel,
  formatQuantity
};
